import { scaleLinear } from 'd3-scale'
import { color } from 'd3-color'

export const MOBILE_BREAKPOINT = 768

/**
 * Determine whether the current viewport is at or below the mobile breakpoint.
 * Returns false when no browser window is available (for example during SSR).
 *
 * @param {number} breakpoint
 * @returns {boolean}
 */
export function isMobile(breakpoint = MOBILE_BREAKPOINT) {
    return typeof window !== 'undefined' && window.innerWidth <= breakpoint
}

//types
/** @typedef {import('../types/core/MapInstance').MapInstance} MapInstance */

// Ensures a <g> element with the specified class exists within the container.
// If it exists, clears its contents; if not, creates it.
export function ensureGroup(container, className) {
    let g = container.select(`g.${className}`)

    if (g.empty()) {
        g = container.append('g').attr('class', className)
    } else {
        g.selectAll('*').remove()
    }

    return g
}

// e.g. to be used with deprecated .style() functions. They will now update CSS classes.
export function updateCSSRule(selector, property, value) {
    // Validate the selector
    if (!selector.startsWith('.') && !selector.startsWith('#')) {
        throw new Error('Invalid selector: Must start with "." for classes or "#" for IDs.')
    }

    // Check if the rule already exists in any stylesheet
    const styleSheets = Array.from(document.styleSheets)
    for (const styleSheet of styleSheets) {
        try {
            const rules = styleSheet.cssRules || styleSheet.rules
            for (const rule of rules) {
                if (rule.selectorText === selector) {
                    // Update the property if the rule exists
                    rule.style[property] = value
                    return
                }
            }
        } catch (e) {
            // Some stylesheets (e.g., cross-origin) may not be accessible
            //console.log(`Could not access rules in stylesheet:`, e)
        }
    }

    // If the rule doesn't exist, create a new stylesheet and add it
    let customSheet = document.getElementById('custom-styles')
    if (!customSheet) {
        customSheet = document.createElement('style')
        customSheet.id = 'custom-styles'
        document.head.appendChild(customSheet)
    }

    // Add the new rule to the custom stylesheet
    try {
        customSheet.sheet.insertRule(`${selector} { ${property}: ${value}; }`, customSheet.sheet.cssRules.length)
    } catch (e) {
        console.error(`Failed to insert rule: ${selector} { ${property}: ${value}; }`, e)
    }
}

export const getFontSizeFromClass = function (className) {
    // Create a temporary element with the specified class
    const tempElement = document.createElement('div')
    tempElement.className = className

    // Apply inline styles to minimize layout interference
    tempElement.style.position = 'absolute'
    tempElement.style.visibility = 'hidden'
    tempElement.style.height = 'auto'
    tempElement.style.width = 'auto'
    tempElement.style.lineHeight = 'normal'
    tempElement.style.fontSize = 'initial'

    // Append directly to the body
    document.body.appendChild(tempElement)

    // Get the computed font-size property and parse it to a number
    const fontSize = parseFloat(window.getComputedStyle(tempElement).fontSize)

    // Remove the temporary element from the document body
    document.body.removeChild(tempElement)

    return fontSize || 0
}

export const getCSSPropertyFromClass = function (className, propertyName) {
    // Create a temporary element with the specified class
    const tempElement = document.createElement('div')
    tempElement.className = className

    // Apply inline styles to minimize layout interference
    tempElement.style.position = 'absolute'
    tempElement.style.visibility = 'hidden'
    tempElement.style.height = 'auto'
    //tempElement.style.width = 'auto'
    tempElement.style.lineHeight = 'normal'

    // Append directly to the body
    document.body.appendChild(tempElement)

    // Get the computed value of the specified property
    const propertyValue = window.getComputedStyle(tempElement).getPropertyValue(propertyName)

    // Remove the temporary element from the document body
    document.body.removeChild(tempElement)

    return propertyValue || null
}

// Helper function to get all CSS rules defined in the document
function getAllCSSRules() {
    let cssRules = []
    for (let sheet of document.styleSheets) {
        try {
            // Some stylesheets may not be accessible due to CORS, so we catch any errors
            for (let rule of sheet.cssRules) {
                cssRules.push(rule)
            }
        } catch (e) {
            console.warn('Unable to access stylesheet:', sheet.href, e)
        }
    }
    return cssRules
}

// Helper function to get explicitly defined styles from CSS for an element
function getStylesFromCSS(element) {
    let matchedRules = []
    const cssRules = getAllCSSRules()

    cssRules.forEach((rule) => {
        if (element.matches(rule.selectorText)) {
            matchedRules.push(rule.style)
        }
    })

    // Create an object of the explicitly set styles
    let explicitStyles = {}
    matchedRules.forEach((style) => {
        for (let i = 0; i < style.length; i++) {
            const property = style[i]
            explicitStyles[property] = style.getPropertyValue(property)
        }
    })

    return explicitStyles
}

// Helper function to apply inline styles explicitly set in CSS. Useful for exporting SVGs with CSS styles.
export const applyInlineStylesFromCSS = (svgElement) => {
    const allElements = svgElement.querySelectorAll('*')

    allElements.forEach((element) => {
        const cssStyles = getStylesFromCSS(element)

        // Apply each explicitly defined CSS style as an inline style
        Object.keys(cssStyles).forEach((property) => {
            const value = cssStyles[property]

            // Check if the property already has an inline style
            const existingInlineStyle = element.style.getPropertyValue(property)

            if (!existingInlineStyle && value) {
                // If no existing inline style, set the new style
                element.style.setProperty(property, value)
            }
        })
    })
}

/**
 * Copy computed styles (a curated property list) into inline styles for all elements
 * so the serialized SVG renders the same as in-browser.
 *
 * Usage: call this right before serialize(svg) or rasterize(svg)
 */
export function applyComputedStylesToSVG(svg) {
    if (!svg || !(svg instanceof SVGElement)) return

    const PAINT_PROPS = new Set([
        'fill',
        'fill-opacity',
        'fill-rule',
        'stroke',
        'stroke-width',
        'stroke-opacity',
        'stroke-linecap',
        'stroke-linejoin',
        'stroke-dasharray',
        'stroke-dashoffset',
    ])

    const PROPS = [
        // painting (includes PAINT_PROPS)
        ...Array.from(PAINT_PROPS),
        // geometry / alignment
        'vector-effect',
        'shape-rendering',
        'text-anchor',
        'dominant-baseline',
        // text
        'font-family',
        'font-size',
        'font-style',
        'font-weight',
        'letter-spacing',
        'word-spacing',
        'line-height',
        // layout / visibility
        'opacity',
        'visibility',
        'display',
        'mix-blend-mode',
        // filters, marker, etc
        'filter',
        'marker-start',
        'marker-mid',
        'marker-end',
        // others
        'transform',
    ]

    function inlineComputedStyle(el) {
        const cs = window.getComputedStyle(el)

        PROPS.forEach((p) => {
            try {
                const v = cs.getPropertyValue(p)

                // skip empty / trivial values
                if (!v || v === '' || v === 'initial') return

                // Allow 'none' for paint properties (fill/stroke) — these are meaningful
                if (v === 'none' && !PAINT_PROPS.has(p)) return

                // Skip '0px' for properties where that is meaningless (but keep it for stroke-width)
                if (v === '0px' && p !== 'stroke-width') return

                // Only set if not already present as inline style
                if (!el.style.getPropertyValue(p)) {
                    el.style.setProperty(p, v)
                }
            } catch (e) {
                // ignore properties that error for this element
            }
        })

        // for text elements ensure font-size is captured if computed
        if (el.tagName && el.tagName.toLowerCase() === 'text') {
            const fs = cs.getPropertyValue('font-size')
            if (fs && !el.style.getPropertyValue('font-size')) el.style.setProperty('font-size', fs)
        }
    }

    inlineComputedStyle(svg)
    svg.querySelectorAll('*').forEach((el) => inlineComputedStyle(el))
}
export function ensureSvgSize(svg) {
    const rect = svg.getBoundingClientRect()
    // Use integer pixel width/height
    const w = Math.round(rect.width) || parseInt(svg.getAttribute('width')) || 800
    const h = Math.round(rect.height) || parseInt(svg.getAttribute('height')) || 600

    // If svg already has viewBox, preserve it; else set viewBox to current content box
    if (!svg.hasAttribute('viewBox')) {
        svg.setAttribute('viewBox', `0 0 ${w} ${h}`)
    }
    svg.setAttribute('width', w)
    svg.setAttribute('height', h)
}

/**
 * Return a GeoJSON feature representing a bounding box, with multipoint geometry.
 * This bounding box is an array like the one in topojson bbox element.
 * [xmin,ymin,xmax,ymax]
 * This is useful for to call d3.fitSize([w, h], getTopoJSONExtentAsGeoJSON(topo.bbox)))
 *
 * @param {*} bb The bounding box [xmin,ymin,xmax,ymax]. For topojson data, just give the topojson.bbox element.
 */
export const getBBOXAsGeoJSON = function (bb) {
    return {
        type: 'Feature',
        geometry: {
            type: 'MultiPoint',
            coordinates: [
                [bb[0], bb[1]],
                [bb[2], bb[3]],
            ],
        },
    }
}

// indexing

/**
 * Index JSONStat stat values by 'geo' code.
 * Return a structure like: {geo:{value:0,status:""}}
 *
 * @param {*} jsData The JSONStat data to index
 */
export const jsonstatToIndex = function (jsData) {
    const ind = {}
    const geos = jsData.Dimension('geo').id
    for (let i = 0; i < geos.length; i++) ind[geos[i]] = jsData.Data(i)
    return ind
}

/**
 * Index CSV stat values by 'geo' code.
 * Return a structure like: {geo:{value:0,status:""}}
 *
 * @param {*} csvData The CSV data to index
 * @param {*} geoCol The name of the geo column in the CSV data
 * @param {*} valueCol The name of the statistical value column in the CSV file.
 */
export const csvToIndex = function (csvData, geoCol, valueCol) {
    const ind = {}
    for (let i = 0; i < csvData.length; i++) {
        const d = csvData[i]
        const v = d[valueCol]
        if (!v) {
            ind[d[geoCol]] = { value: ':', status: '' }
        } else {
            ind[d[geoCol]] = { value: isNaN(+v) ? v : +v, status: '' }
        }
    }
    return ind
}

/**
 * @description returns string with space as thousand separator
 * @function spaceAsThousandSeparator
 * @param {number} number
 */
export const spaceAsThousandSeparator = function (number) {
    return number.toLocaleString('en').replace(/,/gi, ' ')
}

/**
 * Formats a raw number with space as a thousand separator without any rounding.
 * @function formatRawValue
 * @param {number} value
 * @returns {string}
 */
export const formatRawValue = function (value) {
    if (value === null || value === undefined || isNaN(value)) {
        return '0'
    }
    const parts = String(value).split('.')
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
    return parts.join('.')
}

//REST API
export const getEstatRestDataURLBase = 'https://ec.europa.eu/eurostat/api/dissemination/statistics/1.0/data/' //the different dataswag endpoints are appended to this base URL

// This function builds a URL to fetch data from the Eurobase REST API.

/**
 * Build URL to fetch data from eurobase REST API.
 * @param {string} datasetCode The Eurobase dataset code
 * @param {object=} filters The filter parameters as for example: {key:value,key:[value1,value2,value3]}
 * @param {number=} lang
 * @param {number=} format
 * @param {number=} version
 */
export const getEstatDataURL = function (datasetCode, filters, lang, format) {
    lang = lang || 'en'
    format = format || 'json'
    var url = []
    url.push(getEstatRestDataURLBase, datasetCode, '?', 'format=', format, '&lang=', lang)
    if (filters)
        for (var param in filters) {
            var o = filters[param]
            if (Array.isArray(o)) for (var i = 0; i < o.length; i++) url.push('&', param, '=', o[i])
            else url.push('&', param, '=', o)
        }
    return url.join('')
}

/**
 * @param {string} name
 * @returns {string}
 */
export const getURLParameterByName = function (name) {
    name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]')
    var regex = new RegExp('[\\?&]' + name + '=([^&#]*)'),
        results = regex.exec(location.search)
    return !results ? null : decodeURIComponent(results[1].replace(/\+/g, ' '))
}

export const checkIfDiverging = function (map) {
    return map.pointOfDivergence_ !== null
}

/**
 * Prefix used to tag a region's `ecl` attribute when its raw value matches a layer's
 * configured extra category (e.g. "no railway lines") instead of a numeric classification
 * bin. Lets a small set of categorical exceptions be mixed into an otherwise numeric/discrete
 * layer (choropleth) without needing a second base layer.
 */
export const CATEGORY_ECL_PREFIX = 'cat__'

export function toCategoryEcl(rawValue) {
    return CATEGORY_ECL_PREFIX + rawValue
}

export function fromCategoryEcl(ecl) {
    return typeof ecl === 'string' && ecl.startsWith(CATEGORY_ECL_PREFIX) ? ecl.slice(CATEGORY_ECL_PREFIX.length) : undefined
}

/**
 * Wraps a diverging color interpolator so that it centers around a meaningful divergence point.
 * Ensures that transformed values are mapped symmetrically around the divergence point (e.g. 0).
 *
 * @param {function} colorFunc - A diverging color interpolator (e.g. d3.interpolateRdBu)
 * @param {[number, number]} domain - Raw data domain (min, max) before transformation
 * @param {number} pointOfDivergence - The central value of interest (e.g. 0 for positive vs. negative)
 * @param {function} transform - A value transform function (e.g. Math.log, Math.asinh)
 * @returns {function} - A color function that accepts raw values and returns color strings
 */
export const centerDivergingColorFunction = function (colorFunc, domain, pointOfDivergence = 0, transform = (d) => d) {
    // Apply valueTransform to all domain boundaries and divergence point
    const valueTransform = transform || ((d) => d)

    const tMin = valueTransform(domain[0]) // Transformed lower bound
    const tMax = valueTransform(domain[1]) // Transformed upper bound
    const tDiv = valueTransform(pointOfDivergence) // Transformed center/divergence point

    // Create a diverging scale: [min → center → max] → [0 → 0.5 → 1]
    const scaled = scaleLinear().domain([tMin, tDiv, tMax]).range([0, 0.5, 1]).clamp(true) // Prevent extrapolation beyond 0–1

    // Return a function that applies the transform and maps through the scaled color interpolator
    return function (value) {
        return colorFunc(scaled(valueTransform(value)))
    }
}

/**
 * Generates a unique DOM ID
 * @param {string} prefix - Prefix for the ID
 * @returns {string} Unique ID
 */
export function generateUniqueId(prefix) {
    return `${prefix}-${Math.random().toString(36).substr(2, 9)}`
}

//flags
export const flags = {
    b: 'break in time series',
    c: 'confidential',
    d: 'definition differs, see metadata',
    e: 'estimated',
    f: 'forecast',
    n: 'not significant',
    p: 'provisional',
    r: 'revised',
    s: 'Eurostat estimate',
    u: 'low reliability',
    z: 'not applicable',
}

/**
 * @description Executes a function for all inset maps. Some insets might be external SVGs which is why this function was created.
 * @param {*} insets map.insets
 * @param {*} mainSvgId the ID of the map's svg
 * @param {*} functionToExecute the function to execute for each inset
 * @param {*} [parameter=null] the parameter to pass to the function
 * @param {*} [parameter2=null] the parameter to pass to the function
 */
export const executeForAllInsets = function (insets, mainSvgId, functionToExecute, parameter = null, parameter2 = null) {
    for (const geo in insets) {
        const insetGroup = insets[geo]

        if (Array.isArray(insetGroup)) {
            insetGroup.forEach((inset) => {
                // Handle nested arrays for multiple insets with the same geo
                if (Array.isArray(inset)) {
                    inset.forEach((nestedInset) => {
                        if (nestedInset.svgId_ !== mainSvgId) {
                            functionToExecute(nestedInset, parameter, parameter2)
                        }
                    })
                } else {
                    if (inset.svgId_ !== mainSvgId) {
                        functionToExecute(inset, parameter, parameter2)
                    }
                }
            })
        } else {
            // Apply functionToExecute to unique inset
            if (insetGroup.svgId_ !== mainSvgId) {
                functionToExecute(insetGroup, parameter, parameter2)
            }
        }
    }
}

export const upperCaseFirstLetter = (string) => `${string.slice(0, 1).toUpperCase()}${string.slice(1)}`

export const lowerCaseAllWordsExceptFirstLetters = (string) =>
    string.replaceAll(/\S*/g, (word) => `${word.slice(0, 1)}${word.slice(1).toLowerCase()}`)

export function getDownloadURL(svgNode) {
    // Create XML header to ensure the SVG is recognized properly
    const xmlHeader = '<?xml version="1.0" encoding="UTF-8" standalone="no"?>\n'

    // Serialize SVG (outerHTML keeps entities)
    let svgContent = xmlHeader + svgNode.outerHTML

    // --- 🔒 Sanitize invalid HTML entities ---
    svgContent = svgContent
        .replace(/&nbsp;/g, '&#160;') // non-breaking space
        .replace(/&copy;/g, '&#169;') // © if used anywhere
        .replace(/&reg;/g, '&#174;') // ® if used anywhere
        .replace(/&times;/g, '&#215;') // × sign if used in labels
        .replace(/&deg;/g, '&#176;') // ° degrees symbol if used

    // Create blob and return URL
    const svgBlob = new Blob([svgContent], { type: 'image/svg+xml;charset=utf-8' })
    const svgUrl = URL.createObjectURL(svgBlob)
    return svgUrl
}

// Rasterize function with additional error handling
export function serialize(svg) {
    const xmlns = 'http://www.w3.org/2000/xmlns/'
    const xlinkns = 'http://www.w3.org/1999/xlink'
    const svgns = 'http://www.w3.org/2000/svg'
    const fragment = window.location.href + '#'
    const walker = document.createTreeWalker(svg, NodeFilter.SHOW_ELEMENT, null, false)
    while (walker.nextNode()) {
        for (const attr of walker.currentNode.attributes) {
            if (attr.value.includes(fragment)) {
                attr.value = attr.value.replace(fragment, '#')
            }
        }
    }
    svg.setAttributeNS(xmlns, 'xmlns', svgns)
    svg.setAttributeNS(xmlns, 'xmlns:xlink', xlinkns)
    const serializer = new window.XMLSerializer()
    const string = serializer.serializeToString(svg)
    return new Blob([string], { type: 'image/svg+xml' })
}

// adapted from https://observablehq.com/@mbostock/saving-sv
//svg to canvas blob promise
export function rasterize(svg) {
    let resolve, reject
    const promise = new Promise((y, n) => ((resolve = y), (reject = n)))
    const image = new Image()
    image.onerror = reject
    image.onload = () => {
        const rect = svg.getBoundingClientRect()
        const canvas = document.createElement('canvas')
        canvas.width = rect.width
        canvas.height = rect.height
        const context = canvas.getContext('2d')
        context.drawImage(image, 0, 0, rect.width, rect.height)
        context.canvas.toBlob(resolve)
    }
    image.src = URL.createObjectURL(serialize(svg))
    return promise
}

/**
 * Get a URL parameter by name.
 *
 * @param {string} name
 * @returns {string | null}
 */
export function getParameterByName(name) {
    name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]')
    let regex = new RegExp('[\\?&]' + name + '=([^&#]*)'),
        results = regex.exec(location.search)
    return !results ? null : decodeURIComponent(results[1].replace(/\+/g, ' '))
}

export const hexToRgb = (hex) => {
    hex = hex.replace('#', '')
    if (hex.length === 3) {
        hex = hex
            .split('')
            .map((h) => h + h)
            .join('')
    }
    const int = parseInt(hex, 16)
    return [(int >> 16) & 255, (int >> 8) & 255, int & 255]
}

//blends two colors using 'multiply' blending mode. Returns the blended color as an RGB string
export const multiplyBlendMultipleHex = (colors) => {
    // Convert hex color to RGB
    const hexToRgb = (hex) => {
        hex = hex.replace('#', '')
        if (hex.length === 3) {
            hex = hex
                .split('')
                .map((h) => h + h)
                .join('')
        }
        const int = parseInt(hex, 16)
        return [(int >> 16) & 255, (int >> 8) & 255, int & 255]
    }

    // Convert RGB to hex
    const rgbToHex = ([r, g, b]) => `#${[r, g, b].map((c) => c.toString(16).padStart(2, '0')).join('')}`

    // Convert all hex colors to RGB arrays
    const rgbColors = colors.map(hexToRgb)

    // Initialize the result with the first color
    let blended = [...rgbColors[0]]

    // Sequentially multiply each color with the result
    for (let i = 1; i < rgbColors.length; i++) {
        blended = blended.map((v, idx) => Math.round((v / 255) * (rgbColors[i][idx] / 255) * 255))
    }

    // Return the blended color as a hex code
    return rgbToHex(blended)
}

export function averageBlendHex(colors) {
    const parsed = colors.map((c) => color(c).rgb())
    const avg = {
        r: parsed.reduce((sum, c) => sum + c.r, 0) / parsed.length,
        g: parsed.reduce((sum, c) => sum + c.g, 0) / parsed.length,
        b: parsed.reduce((sum, c) => sum + c.b, 0) / parsed.length,
    }
    return `rgb(${avg.r},${avg.g},${avg.b})`
}

/**
 * interpolateIntensity
 *
 * Adjusts the brightness of a base color based on a class index or relative position.
 * Typically used to differentiate quantile levels (e.g., low, medium, high) by making
 * lower classes appear lighter and higher classes closer to the base hue.
 *
 * @param {string} baseColor - The original color to adjust (hex or CSS string).
 * @param {number} idx - The class index (0 for lowest, 1 for mid, 2 for highest in 3-class scales).
 * @returns {string} - The adjusted color as a hex string.
 */
export function interpolateIntensity(baseColor, idx) {
    const f = idx / 2 // 0, 0.5, 1
    const c = color(baseColor)
    return c.brighter(1.5 - f).formatHex() // make low brighter
}

// convert rect attributes into an SVG path string
// used for workaround whereby clipPaths which use rect elements do not work in adobe illustrator
export const convertRectanglesToPaths = function (x, y, width, height) {
    var x = parseFloat(x, 10)
    var y = parseFloat(y, 10)
    var width = parseFloat(width, 10)
    var height = parseFloat(height, 10)

    if (x < 0 || y < 0 || width < 0 || height < 0) {
        return ''
    }

    return 'M' + x + ',' + y + 'L' + (x + width) + ',' + y + ' ' + (x + width) + ',' + (y + height) + ' ' + x + ',' + (y + height) + 'z'
}

export const getTextColorForBackground = function (backgroundColor) {
    if (!backgroundColor) return 'black'

    let r, g, b

    // Create a temporary element to handle named colors
    if (!backgroundColor.startsWith('rgb') && !backgroundColor.startsWith('#')) {
        const tempDiv = document.createElement('div')
        tempDiv.style.color = backgroundColor
        document.body.appendChild(tempDiv)
        const computedColor = window.getComputedStyle(tempDiv).color
        document.body.removeChild(tempDiv)
        backgroundColor = computedColor // Convert named color to RGB
    }

    // Handle hex colors like '#FFFFFF' or '#FFF'
    if (backgroundColor.startsWith('#')) {
        let hex = backgroundColor.replace('#', '')
        if (hex.length === 3) {
            hex = hex
                .split('')
                .map((c) => c + c)
                .join('')
        }
        r = parseInt(hex.substring(0, 2), 16)
        g = parseInt(hex.substring(2, 4), 16)
        b = parseInt(hex.substring(4, 6), 16)
    } else {
        // Handle RGB colors like 'rgb(255,255,255)'
        const rgb = backgroundColor.match(/\d+/g)
        r = parseInt(rgb[0])
        g = parseInt(rgb[1])
        b = parseInt(rgb[2])
    }

    // Calculate luminance
    const luminance = 0.2126 * (r / 255) + 0.7152 * (g / 255) + 0.0722 * (b / 255)

    // Return black for light backgrounds, white for dark backgrounds
    return luminance > 0.5 ? 'black' : 'white'
}

// get css selector for data-driven regions. Different maps have different selectors for their regions.
export const getRegionsSelector = (map) => {
    if (map.Geometries.userGeometries) return '#em-user-regions path'
    if (map.gridCartogram_) return '#em-grid-container .em-grid-cell'
    if (map.geo_ === 'WORLD') return '#em-worldrg path'

    // Map types that need to exclude RS and EL cntrg regions (because they include Kosovo and Athos, which are not part of the NUTS regions)
    const mapsExcludingRSEL = ['ch', 'chbi', 'chtri', 'coxcomb', 'ps', 'pie']

    if (map.nutsLevel_ === 'mixed') {
        if (mapsExcludingRSEL.includes(map._mapType)) {
            return '#em-mixed-nutsrg path:not(#em-cntrg-RS):not(#em-cntrg-EL), #em-cntrg path:not(#em-cntrg-RS):not(#em-cntrg-EL)'
        }
        return '#em-mixed-nutsrg path, #em-cntrg path'
    }

    if (mapsExcludingRSEL.includes(map._mapType)) {
        return '#em-nutsrg path:not(#em-cntrg-RS):not(#em-cntrg-EL), #em-cntrg path:not(#em-cntrg-RS):not(#em-cntrg-EL)'
    }

    return '#em-nutsrg path, #em-cntrg path'
}

// get css selector for legend mouse hover. Different maps have different selectors for their regions
export const getLegendRegionsSelector = (map) => {
    if (map.Geometries.userGeometries) return '#em-user-regions'
    if (map.gridCartogram_) return '#em-grid-container'
    if (map.geo_ === 'WORLD') return '#em-worldrg'
    if (map.nutsLevel_ === 'mixed') return '#em-mixed-nutsrg, #em-cntrg'
    return '#em-nutsrg, #em-cntrg'
}

// Helper functions to calculate current view as bbox

/**
 * Approximate geographic bbox based on map.position_ (linear, equirectangular assumption)
 * Works before projection is created.
 * @returns [minLon, minLat, maxLon, maxLat]
 */
export const getApproxCurrentGeoBbox = function (map) {
    const halfWidth = 0.5 * map.position_.z * map.width_
    const halfHeight = 0.5 * map.position_.z * map.height_
    return [map.position_.x - halfWidth, map.position_.y - halfHeight, map.position_.x + halfWidth, map.position_.y + halfHeight]
}

export function getRegionById(map, id) {
    const svg = map.svg_ || map.svg()
    let region = null

    if (map.gridCartogram_) {
        svg.selectAll('#em-grid-container .em-grid-cell').each(function (d) {
            if (d?.properties?.id === id) region = d
        })
    } else {
        svg.selectAll('path').each(function (d) {
            if (d?.properties?.id === id) region = d
        })
    }

    return region
}

export const compactFormatter = new Intl.NumberFormat('en', {
    notation: 'compact',
    compactDisplay: 'short',
    maximumFractionDigits: 1,
})
export const longFormatter = new Intl.NumberFormat('en', {
    notation: 'compact',
    compactDisplay: 'long',
    maximumFractionDigits: 1,
})

/**
 * Rounds a threshold value to the nearest "nice" number (1, 1.5, 2, 2.5, 3, 4, 5 … × 10^n).
 * @param {number} value
 * @param {number} min
 * @param {number} max
 * @returns {number}
 */
export function roundToNiceNumber(value, min, max) {
    if (value === 0) return 0

    const sign = value < 0 ? -1 : 1
    const absValue = Math.abs(value)
    const exponent = Math.floor(Math.log10(absValue))
    const magnitude = Math.pow(10, exponent)
    const fraction = absValue / magnitude

    const niceFractions = [1, 1.5, 2, 2.5, 3, 4, 5, 6, 7, 8, 9, 10]
    let closestFraction = niceFractions[0]
    let minDiff = Math.abs(fraction - niceFractions[0])
    for (const nf of niceFractions) {
        const diff = Math.abs(fraction - nf)
        if (diff < minDiff) {
            minDiff = diff
            closestFraction = nf
        }
    }

    const niceValue = sign * closestFraction * magnitude
    const margin = (max - min) * 0.01
    if (niceValue < min - margin) return min
    if (niceValue > max + margin) return max
    return niceValue
}

/**
 * Adjusts thresholds when rounding caused duplicates by splitting the largest gap.
 * @param {number[]} uniqueThresholds
 * @param {number} targetCount
 * @param {number} min
 * @param {number} max
 * @returns {number[]}
 */
export function adjustDuplicateThresholds(uniqueThresholds, targetCount, min, max) {
    if (uniqueThresholds.length >= targetCount) return uniqueThresholds.slice(0, targetCount)

    const result = [...uniqueThresholds]

    while (result.length < targetCount) {
        let maxGap = 0
        let maxGapIndex = -1

        for (let i = 0; i < result.length - 1; i++) {
            const gap = result[i + 1] - result[i]
            if (gap > maxGap) {
                maxGap = gap
                maxGapIndex = i
            }
        }

        const startGap = result[0] - min
        const endGap = max - result[result.length - 1]

        if (startGap > maxGap && startGap > endGap) {
            const newValue = roundToNiceNumber((min + result[0]) / 2, min, max)
            if (!result.includes(newValue) && newValue > min) {
                result.unshift(newValue)
                result.sort((a, b) => a - b)
                continue
            }
        }

        if (endGap > maxGap) {
            const newValue = roundToNiceNumber((result[result.length - 1] + max) / 2, min, max)
            if (!result.includes(newValue) && newValue < max) {
                result.push(newValue)
                result.sort((a, b) => a - b)
                continue
            }
        }

        if (maxGapIndex >= 0) {
            const midpoint = (result[maxGapIndex] + result[maxGapIndex + 1]) / 2
            const newValue = roundToNiceNumber(midpoint, min, max)
            result.push(!result.includes(newValue) ? newValue : midpoint)
        } else {
            const step = (max - min) / (targetCount + 1)
            result.push(min + step * (result.length + 1))
        }

        result.sort((a, b) => a - b)
    }

    return result.slice(0, targetCount)
}

/**
 * Rounds classification thresholds to nice, legible values.
 * @param {number[]} thresholds - raw threshold values from the classifier
 * @param {number[]} values - full data array (used to derive min/max bounds)
 * @returns {number[]}
 */
export function applyNiceNumbers(thresholds, values) {
    if (!thresholds || thresholds.length === 0) return []

    const min = Math.min(...values)
    const max = Math.max(...values)

    const niceThresholds = thresholds.map((t) => roundToNiceNumber(t, min, max))
    const uniqueSorted = [...new Set(niceThresholds)].sort((a, b) => a - b)

    if (uniqueSorted.length < thresholds.length) {
        return adjustDuplicateThresholds(uniqueSorted, thresholds.length, min, max)
    }

    return uniqueSorted
}
